<template>
  <div class="mt-4 md:mt-5 pb-12">
    <div
      class="h1 font-secondary felx mt-4 mb-4 md:mb-4"
      data-i18n="charterDestinationHoteltitle"
    >
      {{ $t('charterDestinationHoteltitle') }}
    </div>
    <p
      v-if="getHotelIngress"
      class="felx mt-4 mb-12"
    >
      {{ getHotelIngress }}
    </p>
    <template v-if="!hydrated || loading || !packages">
      <HotelPackageDateRowSkeleton
        v-for="idx in 6"
        :key="idx"
        class="mb-1"
      />
    </template>
    <template v-else>
      <div class="md:w-4/12 lg:w-3/12 mb-4 px-0">
        <DestinationHotelSorting @update="fetch" />
      </div>
      <InfiniteScroll
        :scroll-to-button="false"
        @fetch="nextPage"
      >
        <template #content>
          <template v-if="slicedPackages.length">
            <DestinationHotelTabCard
              v-for="hotelPackage in slicedPackages"
              :key="hotelPackage.id"
              :package="hotelPackage"
            />
          </template>
          <div
            v-else
            class="flex items-center justify-center min-h-[150px] border-t border-b text-slate-500"
          >
            <Alert
              class="w-12/12 mt-6"
              type="light"
            >
              <template #icon>
                <fa
                  class="svg-inline--fa fa-w-16 fa-5x"
                  :icon="faPlaneDeparture"
                />
              </template>
              <template #default>
                <strong class="text-black">
                  {{ $t('charterCampaignNoneFound') }}
                </strong>
                <div class="mt-4">
                  <button
                    class="btn btn-orange"
                    type="button"
                    @click="fetchCampaignPackages"
                  >
                    {{ $t('charterCampaignNoneFoundNewSearch') }}
                  </button>
                </div>
              </template>
            </Alert>
          </div>
        </template>
      </InfiniteScroll>
    </template>
  </div>
</template>

<script setup>
import { faPlaneDeparture } from '@fortawesome/pro-duotone-svg-icons'

const hydrated = ref(false)
const PACKAGES_PER_PAGE = 15
const packageCount = ref(PACKAGES_PER_PAGE)

const props = defineProps({
  destinationId: {
    type: Number,
    default: 0,
  },

  currentId: {
    type: [Number, String],
    default: null,
  },

  filledSections: {
    type: Array,
    default: () => [],
  },
})

const packages = ref(null)
const loading = ref(true)

const localeStore = useLocaleStore()
const charterPackagesStore = useCharterPackagesStore()
const charterdestinationStore = useCharterDestinationStore()
const { charterDestination } = storeToRefs(charterdestinationStore)
const { filters, sortBy } = storeToRefs(charterPackagesStore)
const { t } = useI18n()

const slicedPackages = computed(() => {
  return packages.value.slice(0, packageCount.value)
})

const nextPage = () => {
  packageCount.value += PACKAGES_PER_PAGE
}

onBeforeMount(() => {
  fetch()
})

const getFilteredSection = (filterString, includeAll) => {
  const section = props.filledSections.find(section => section.type === filterString)

  if (section && !includeAll) {
    return section.data
  }

  return section || null
}

const getHotelIngress = computed(() => {
  return getFilteredSection('hotel_list').ingress || t('charterDestinationHotelSubtext')
})

const fetch = async () => {
  const destinationId = props.destinationId > 0 ? props.destinationId : charterDestination.value.destination_id
  loading.value = true
  const { data: result } = await apiFetch(
    `/${localeStore.locale}/charter/packages/offers/search`, {
      method: 'post',
      timeout: 60000,
      body: {
        destinations: [destinationId],
        tripLength: {
          'max': 9,
          'min': 7,
        },
        mode: 'hotelList',
      },
    }
  )

  await Promise.all([
    charterPackagesStore.getFilters(),
  ])

  const PACKAGE_SORT_KEY = 'pricepp'
  const filteredResult = result.filter(r => `${r?.giata_id}` !== `${props.currentId}`)

  const sortPackages = (a, b) => {
    switch (sortBy.value) {
      case 'premium':
        if (a.recommended && b.recommended) {
          return b.tripadvisor_rating - a.tripadvisor_rating
        }
        if (a.recommended !== b.recommended) {
          return a.recommended ? -1 : 1
        }
        return a[PACKAGE_SORT_KEY] - b[PACKAGE_SORT_KEY]
      case 'price':
        return a[PACKAGE_SORT_KEY] - b[PACKAGE_SORT_KEY]
      case '-price':
        return b[PACKAGE_SORT_KEY] - a[PACKAGE_SORT_KEY]
      case 'tripadvisor_rating':
        if (a.tripadvisor_rating === b.tripadvisor_rating) {
          return a[PACKAGE_SORT_KEY] - b[PACKAGE_SORT_KEY]
        }
        return b.tripadvisor_rating - a.tripadvisor_rating
      default:
        if (a.recommended === b.recommended) {
          return a[PACKAGE_SORT_KEY] - b[PACKAGE_SORT_KEY]
        }
        return a.recommended ? -1 : 1
    }
  }

  const sortedPackages = computed(() => {
    return ([...filteredResult] || []).sort(sortPackages)
  })

  const filteredPackages = computed(() => {
    return sortedPackages.value.map((pkg) => {
      if (!filters.value?.charter_tags) {
        return pkg
      }
      return {
        ...pkg,
        charter_tags: (pkg.charter_tags || []).map((tagId) => {
          if (!filters.value?.charter_tags) {
            return tagId // return the original tagId if charter_tags is undefined
          }
          return filters.value?.charter_tags.find(tag => tag.id === tagId)
        }),
      }
    })
  })
  packages.value = filteredPackages.value || []

  loading.value = false
}

onMounted(() => {
  hydrated.value = true
})
</script>